/**
 * @file ApplyScopeControl.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-30
 * @modified 2026-06-30
 * @description Shared "Applies to" pricing-group scope control used by every
 *              contract-pricing modal (contracts, milk powder, straights). A
 *              compact segmented control replaces the old radio pills (and the
 *              duplicated .apply-scope-* CSS that bled hover styling between
 *              source files). When the chosen customer is a child account it
 *              explains the parent/child relationship and offers a one-click
 *              switch to the parent so the price can cover the group. Renders a
 *              read-only summary in view mode.
 */

import React, { useEffect } from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';
import './ApplyScopeControl.css';

const SEGMENTS = [
  { value: 'group', label: 'Whole group' },
  { value: 'parent', label: 'Parent only' },
  { value: 'specific', label: 'Specific' },
];

const idStr = (v) => (v == null ? '' : String(v));

function ApplyScopeControl({
  members = [],
  accountId,
  applyScope = 'parent',
  scopeAccountIds = [],
  onScopeChange,
  onAccountsChange,
  onSelectAccount,
  readOnly = false,
}) {
  const inGroup = Array.isArray(members) && members.length > 1;
  const parent = inGroup ? (members.find((m) => m.is_parent) || members[0]) : null;
  const selected = inGroup ? members.find((m) => idStr(m.id) === idStr(accountId)) : null;
  const isChildSelected = !!(selected && !selected.is_parent);

  // A child-owned price can only apply to that child, so keep scope at 'parent'
  // (= "this account only") while a child is the selected customer.
  useEffect(() => {
    if (!readOnly && inGroup && isChildSelected && applyScope !== 'parent' && onScopeChange) {
      onScopeChange('parent');
    }
  }, [readOnly, inGroup, isChildSelected, applyScope, onScopeChange]);

  if (!inGroup) return null;

  const childCount = members.length - 1;

  // ── Child account selected ────────────────────────────────────────────────
  if (isChildSelected) {
    return (
      <div className="scope-applies">
        <div className="scope-applies-label">Applies to</div>
        <div className="scope-child">
          <span className="material-symbols-rounded">account_tree</span>
          <div className="scope-child-text">
            <span className="scope-name">{selected.company_name}</span> is a child of{' '}
            <span className="scope-name">{parent.company_name}</span>. This price applies to{' '}
            <strong>{selected.company_name} only</strong>.
            {!readOnly && onSelectAccount && (
              <button
                type="button"
                className="scope-switch"
                onClick={() => { onSelectAccount(parent.id); if (onScopeChange) onScopeChange('group'); }}
              >
                Switch to {parent.company_name} to price the group
                <span className="material-symbols-rounded">arrow_forward</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Parent selected ───────────────────────────────────────────────────────
  // The same control renders in view and edit; in view the buttons / multi-select
  // are simply disabled (not interactive) so it reads consistently.
  const memberOptions = members.map((m) => ({
    value: m.id,
    label: `${m.company_name}${m.account_no ? ` (${m.account_no})` : ''}${m.is_parent ? ' — parent' : ''}`,
    searchText: `${m.account_no || ''} ${m.company_name || ''}`,
  }));

  const segments = (
    <div className="scope-seg" role="group" aria-label="Applies to">
      {SEGMENTS.map((s) => (
        <button
          type="button"
          key={s.value}
          className={`scope-seg-btn ${applyScope === s.value ? 'active' : ''}`}
          disabled={readOnly}
          onClick={() => onScopeChange && onScopeChange(s.value)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  // Specific: segments + hint stacked on the left, the multi-select to the right.
  if (applyScope === 'specific') {
    return (
      <div className="scope-applies">
        <div className="scope-applies-label">Applies to</div>
        <div className="scope-specific-row">
          <div className="scope-seg-col">
            {segments}
            <div className="scope-applies-hint">Applies only to these selected accounts:</div>
          </div>
          <div className="scope-mds">
            <MultiSelectDropdown
              options={memberOptions}
              value={scopeAccountIds}
              onChange={onAccountsChange}
              placeholder="Select accounts this price applies to…"
              searchable
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    );
  }

  // Whole group / Parent only: segments + hint side by side.
  return (
    <div className="scope-applies">
      <div className="scope-applies-label">Applies to</div>
      <div className="scope-inline-row">
        {segments}
        <div className="scope-applies-hint">
          {applyScope === 'group'
            ? `${parent.company_name} + ${childCount} child${childCount !== 1 ? 'ren' : ''}.`
            : `${parent.company_name} only — child accounts are not covered.`}
        </div>
      </div>
    </div>
  );
}

export default ApplyScopeControl;
